/**
 * Researcher Dashboard Component
 *
 * Main dashboard for researchers with tabs for different features.
 */

import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Tabs,
  Tab,
  Paper,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  Science as SimulationIcon,
  Person as PersonaIcon,
  Share as SharedIcon,
  AccountBalance as BudgetIcon,
  Loop as RLLoopIcon
} from '@mui/icons-material';
import BudgetOverview from './BudgetOverview';
import SandboxPersonaManager from './SandboxPersonaManager';
import SharedPersonasView from './SharedPersonasView';
import * as researcherApi from '../../services/researcherApi';

// Import Advanced Simulation Dashboard from admin (reuse)
import AdvancedSimulationDashboard from '../admin/advancedSimulation/AdvancedSimulationDashboard';
// Import RL Loop Dashboard from admin (reuse)
import RLLoopDashboard from '../admin/advancedSimulation/RLLoopDashboard';

const ResearcherDashboard = ({ token, user }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [budgetStatus, setBudgetStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  // Check if user has RL Loop access
  const canAccessRLLoop = user?.researcherInfo?.canAccessRLLoop === true;

  // Build dynamic routes array based on permissions
  const routes = canAccessRLLoop
    ? ['simulations', 'personas', 'shared', 'budget', 'rl-loops']
    : ['simulations', 'personas', 'shared', 'budget'];

  useEffect(() => {
    // Parse route to set active tab
    const pathParts = location.pathname.split('/').filter(Boolean);
    const view = pathParts[2]; // 'simulations', 'personas', 'shared', 'budget', 'rl-loops'

    const tabIndex = routes.indexOf(view);
    if (tabIndex !== -1) {
      setActiveTab(tabIndex);
    } else if (view === 'simulations' || !view) {
      setActiveTab(0);
    }
  }, [location.pathname, routes]);

  useEffect(() => {
    loadBudgetStatus();
  }, []);

  const loadBudgetStatus = async () => {
    try {
      setLoading(true);
      const status = await researcherApi.getBudgetStatus();
      setBudgetStatus(status);
    } catch (err) {
      console.error('Failed to load budget status:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    navigate(`/researcher/dashboard/${routes[newValue]}`);
  };

  const renderTabContent = () => {
    const currentRoute = routes[activeTab];

    switch (currentRoute) {
      case 'simulations':
        return (
          <Box>
            {budgetStatus?.budgetExhausted && (
              <Alert severity="error" sx={{ mb: 2 }}>
                Your budget is exhausted. You cannot start new simulations until an administrator refreshes your budget.
              </Alert>
            )}
            <AdvancedSimulationDashboard token={token} isResearcher={true} />
          </Box>
        );
      case 'personas':
        return <SandboxPersonaManager />;
      case 'shared':
        return <SharedPersonasView />;
      case 'budget':
        return <BudgetOverview />;
      case 'rl-loops':
        return canAccessRLLoop ? (
          <Box>
            {budgetStatus?.budgetExhausted && (
              <Alert severity="error" sx={{ mb: 2 }}>
                Your budget is exhausted. You cannot start new RL Loops until an administrator refreshes your budget.
              </Alert>
            )}
            <RLLoopDashboard token={token} isResearcher={true} />
          </Box>
        ) : null;
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl">
      <Box py={3}>
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4">Researcher Dashboard</Typography>
          {budgetStatus && (
            <Box display="flex" alignItems="center" gap={2}>
              <Typography variant="body2" color="textSecondary">
                Daily: ${budgetStatus.dailyUsage?.toFixed(2) || '0.00'} / ${budgetStatus.dailyLimit?.toFixed(2) || '0.00'}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Monthly: ${budgetStatus.monthlyUsage?.toFixed(2) || '0.00'} / ${budgetStatus.monthlyLimit?.toFixed(2) || '0.00'}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Budget Warning */}
        {budgetStatus?.budgetExhausted && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Your budget is exhausted. Contact an administrator to refresh your budget.
          </Alert>
        )}

        {/* Tabs */}
        <Paper sx={{ mb: 3 }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab
              icon={<SimulationIcon />}
              label="Simulations"
              iconPosition="start"
            />
            <Tab
              icon={<PersonaIcon />}
              label="Sandbox Personas"
              iconPosition="start"
            />
            <Tab
              icon={<SharedIcon />}
              label="Shared with Me"
              iconPosition="start"
            />
            <Tab
              icon={<BudgetIcon />}
              label="Budget"
              iconPosition="start"
            />
            {canAccessRLLoop && (
              <Tab
                icon={<RLLoopIcon />}
                label="RL Loops"
                iconPosition="start"
              />
            )}
          </Tabs>
        </Paper>

        {/* Tab Content */}
        <Box>
          {renderTabContent()}
        </Box>
      </Box>
    </Container>
  );
};

export default ResearcherDashboard;
